import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import Modal from 'LuxKit/Modal/Modal'
import React, { ChangeEventHandler, useCallback, useContext, useState } from 'react'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { connect } from 'react-redux'
import { selectSelectedTravellerEmployees } from 'businessTraveller/selectors/businessTravellerEmployeeSelectors'
import { GlobalSearchStateContext, GlobalSearchDispatchContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { EmptyArray } from 'lib/array/arrayUtils'
import BusinessTravellerMultiSelectContent from './BusinessTravellerMultiSelectContent'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { useAppDispatch } from 'hooks/reduxHooks'
import { resetCheckoutForm } from 'actions/CheckoutActions'
import { saveBusinessTravellerSelectedTravellerState } from 'businessTraveller/storage/businessTravellerStorage'
import { clearFormStateSnapshot } from 'storage/checkout'
import setSelectedBusinessTravellersAndGuests from 'actions/businessTraveller/setSelectedBusinessTravellerEmployeesAndGuests'

interface MappedStateProps {
  travellerEmployees: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>
  selectedTravellerEmployeeIds?: Array<string>
  selectedTravellerGuestCount?: number
}

interface Props {
  modalMode?: 'default' | 'drawer' | 'menu' | 'drawer-starboard'
  modalDismissible?: boolean
  hotelTravellerSelection?: boolean
  onHandleSelectTravellerApply?: (employeeCount: number, guestCount: number) => void
}

function BusinessTravellerSelectModal(props: Props & MappedStateProps) {
  const { travellerEmployees, selectedTravellerEmployeeIds, selectedTravellerGuestCount, modalMode = 'drawer', modalDismissible = true, hotelTravellerSelection = true, onHandleSelectTravellerApply } = props

  const appDispatch = useAppDispatch()
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const { occupancies = EmptyArray } = useContext(GlobalSearchStateContext)

  const [travellerSelectState, setTravellerSelectState] = useState<{ selectedTravellers: Array<string>, guestCount: number }>({
    selectedTravellers: selectedTravellerEmployeeIds ?? [],
    guestCount: selectedTravellerGuestCount ?? 0,
  })

  const hasEmployees = travellerEmployees?.length ?? 0 > 1

  const [searchTerm, setSearchTerm] = useState<string>('')
  const handleSearchTermChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setSearchTerm(event.currentTarget.value)
  }, [])

  const handleOccupancyChange = useCallback((value: number) => {
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_OCCUPANCIES, occupancies: [{ ...occupancies[0], adults: value }] })
  }, [globalSearchDispatch, occupancies])

  const handleSelectTravellerApply = useCallback(() => {
    if (travellerSelectState.selectedTravellers.length === 0 && travellerSelectState.guestCount === 0) {
      showSnackbar('Please select at least one traveller or guest.', 'warning')
      return
    }

    // Because we are now saving the traveller form, we need to make sure its cleared when a new traveller is selected.
    appDispatch(resetCheckoutForm())
    clearFormStateSnapshot()
    // Update employee list and guest count in redux state
    appDispatch(setSelectedBusinessTravellersAndGuests(travellerSelectState.selectedTravellers, travellerSelectState.guestCount))
    handleOccupancyChange(travellerSelectState.selectedTravellers.length + travellerSelectState.guestCount)
    // Save to localstorage so persist on page refresh
    saveBusinessTravellerSelectedTravellerState(travellerSelectState.selectedTravellers, travellerSelectState.guestCount)
  }, [appDispatch, travellerSelectState, handleOccupancyChange])

  const modalElementContext = useModalElementContext()

  const dismiss = useCallback(() => {
    if (!modalDismissible && travellerSelectState.selectedTravellers.length === 0 && travellerSelectState.guestCount === 0) {
      showSnackbar('Please select at least one traveller or guest.', 'warning')
      return
    }
    modalElementContext.resolve()
    handleSelectTravellerApply()
    onHandleSelectTravellerApply?.(travellerSelectState.selectedTravellers.length, travellerSelectState.guestCount)
  }, [modalElementContext, handleSelectTravellerApply, onHandleSelectTravellerApply, travellerSelectState.selectedTravellers.length, travellerSelectState.guestCount, modalDismissible])

  return <Modal
    mode={modalMode}
    title="Who is going?"
    dismissible={modalDismissible}
    primaryActionText="Continue"
    onPrimaryActionClick={dismiss}
    headerExtension={hasEmployees && <TextInputOnly
      value={searchTerm}
      type="text"
      placeholder="Search traveller name"
      onChange={handleSearchTermChange}
      endIcon={<LineSearchIcon />}
      noValidationSpacing
    />}
  >
      <BusinessTravellerMultiSelectContent
        searchTerm={searchTerm}
        travellerSelectState={travellerSelectState}
        setTravellerSelectState={setTravellerSelectState}
        hotelTravellerSelection={hotelTravellerSelection}
      />
  </Modal>
}

export default connect<MappedStateProps, {}, {}, App.State>((state) => {
  return {
    travellerEmployees: selectSelectedTravellerEmployees(state),
    selectedTravellerEmployeeIds: state.businessTraveller.selectedTravellerEmployeeIds,
    selectedTravellerGuestCount: state.businessTraveller.selectedTravellerGuestCount,
  }
})(BusinessTravellerSelectModal)
